'use client'

/**
 * <LaunchCountdown /> (R101, 2026-05-31)
 *
 * Großer, fetter Countdown bis zum öffentlichen Launch am 03.06.2026 07:30
 * Europe/Berlin (= 05:30 UTC im Juni / MESZ). Tickt live im 1-Sekunden-Takt.
 *
 * Wenn der Termin erreicht ist:
 *   - Backend (/api/public/site-state) flippt registrationEnabled + showLogin
 *     + showRegister automatisch auf true (Auto-Flip).
 *   - Frontend zeigt „Die Registrierung ist jetzt offen — willkommen!" und
 *     refresht site-state nach 3 s, damit der Header die Buttons aktualisiert.
 *
 * Props:
 *   launchAt  ISO-UTC-String aus dem Backend (autoritative Source-of-Truth).
 *             Frontend rechnet selbst in Browser-Tageszeit um.
 *   variant   'hero' = sehr groß für Landing-Page, 'inline' = kompakter für
 *             /register-Page.
 */

import { useEffect, useState } from 'react'

export interface LaunchCountdownProps {
  launchAt: string | null | undefined
  variant?: 'hero' | 'inline'
  /** Wird nach Ablauf gerufen, damit Parent ggf. site-state refetched. */
  onElapsed?: () => void
}

interface Remaining {
  days: number
  hours: number
  minutes: number
  seconds: number
  elapsed: boolean
}

function computeRemaining(target: Date): Remaining {
  const now = Date.now()
  const diff = target.getTime() - now
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, elapsed: true }
  }
  const days = Math.floor(diff / (24 * 60 * 60 * 1000))
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000))
  const seconds = Math.floor((diff % (60 * 1000)) / 1000)
  return { days, hours, minutes, seconds, elapsed: false }
}

const pad = (n: number) => n.toString().padStart(2, '0')

export function LaunchCountdown({ launchAt, variant = 'hero', onElapsed }: LaunchCountdownProps) {
  const target = launchAt ? new Date(launchAt) : null
  const [remaining, setRemaining] = useState<Remaining | null>(() =>
    target ? computeRemaining(target) : null,
  )
  const [elapsedFired, setElapsedFired] = useState(false)

  useEffect(() => {
    if (!target) return
    const tick = () => {
      const r = computeRemaining(target)
      setRemaining(r)
      if (r.elapsed && !elapsedFired) {
        setElapsedFired(true)
        onElapsed?.()
      }
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [target?.getTime(), elapsedFired, onElapsed])

  if (!target || !remaining) return null

  // R102.12 (2026-06-02, Stephan-Anweisung Pre-Launch T-2):
  // Nach Ablauf des Countdowns soll die Komponente KOMPLETT verschwinden.
  // Stephan schaltet die Registrierung manuell um — nicht automatisch
  // gekoppelt an den Countdown.
  if (remaining.elapsed) return null

  const targetBerlin = target.toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  // COUNTDOWN — Pre-Launch
  const isHero = variant === 'hero'
  return (
    <div
      className={
        isHero
          ? 'inline-flex flex-col gap-2 px-5 py-4 rounded-2xl border'
          : 'inline-flex flex-col gap-1.5 px-4 py-3 rounded-xl border'
      }
      style={{
        background: 'color-mix(in oklch, var(--award-ink-2) 70%, transparent)',
        borderColor: 'color-mix(in oklch, var(--award-gold) 35%, transparent)',
      }}
    >
      <div
        className="award-eyebrow"
        style={{
          color: 'var(--award-gold)',
          letterSpacing: '.18em',
          fontSize: isHero ? '11px' : '10px',
        }}
      >
        Countdown · Public-Launch
      </div>
      <div
        className={isHero ? 'flex items-baseline gap-3' : 'flex items-baseline gap-2'}
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 900,
          color: 'var(--award-cirrus)',
          lineHeight: 1,
        }}
      >
        <CountUnit value={remaining.days} label="Tage" big={isHero} />
        <Sep big={isHero} />
        <CountUnit value={remaining.hours} label="Std" big={isHero} pad2 />
        <Sep big={isHero} />
        <CountUnit value={remaining.minutes} label="Min" big={isHero} pad2 />
        <Sep big={isHero} />
        <CountUnit value={remaining.seconds} label="Sek" big={isHero} pad2 />
      </div>
      <div
        className="text-[11px]"
        style={{ color: 'var(--award-cirrus-3)' }}
      >
        {targetBerlin} Uhr · Europe/Berlin
      </div>
    </div>
  )
}

function CountUnit({
  value,
  label,
  big,
  pad2 = false,
}: { value: number; label: string; big: boolean; pad2?: boolean }) {
  return (
    <div className="flex flex-col items-center">
      <span
        style={{
          fontSize: big ? 'clamp(36px, 6vw, 64px)' : 'clamp(22px, 4vw, 36px)',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em',
          color: 'var(--award-gold)',
        }}
      >
        {pad2 ? pad(value) : value}
      </span>
      <span
        style={{
          fontSize: big ? '10px' : '9px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.18em',
          color: 'var(--award-cirrus-3)',
          marginTop: '2px',
        }}
      >
        {label}
      </span>
    </div>
  )
}

function Sep({ big }: { big: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        color: 'color-mix(in oklch, var(--award-gold) 45%, transparent)',
        fontSize: big ? 'clamp(28px, 5vw, 52px)' : 'clamp(20px, 3vw, 30px)',
        fontFamily: 'var(--font-display)',
        fontWeight: 900,
        lineHeight: 1,
        transform: 'translateY(-0.05em)',
      }}
    >
      :
    </span>
  )
}
